import React, { useState } from 'react';
import loadable from '@loadable/component';
import type { NavMenuCategoryProps } from '#components/NavMenuCategory';
import NavMenuCategory from '#components/NavMenuCategory';
import LanguageSelector from '#components/LanguageSelector';
import SmartLink from '#components/SmartLink';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { navigate } from 'gatsby';

const PrimaryLogo = loadable(() => import('#components/PrimaryLogo'));

export type NavBarDesktopProps = {
  menu: Omit<NavMenuCategoryProps, 'open' | 'onOpen'>[],
};

const NavBarDesktop = ({
  menu,
}: NavBarDesktopProps) => {
  const isLanguageEnabled = process.env.GATSBY_ENABLE_LANG === 'true';

  // only for large/expanded menus
  const [openLargeMenu, setOpenLargeMenu] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleKeyDown = (e:any) => {
    if (e.key === 'Enter' && searchQuery.trim() !== '') {
      setIsSearching(false);
      setSearchQuery('');
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="w-full min-h-16 grid grid-cols-12">
      <div className="col-span-2 flex items-center">
        <SmartLink slug="/" ariaLabel="Improving Home Page">
          <PrimaryLogo />
        </SmartLink>
      </div>

      {!isSearching ? (
        <div className="col-span-9 flex justify-end items-stretch">
          {menu.map((categoryProps, index) => (
            <React.Fragment key={categoryProps.id}>
              <NavMenuCategory
                key={categoryProps.id}
                open={categoryProps.id === openLargeMenu}
                onOpen={setOpenLargeMenu}
                {...categoryProps}
              />
              {index === menu.length - 2 && (
              <button
                type="button"
                onClick={() => setIsSearching(true)}
                aria-label="search"
                className="p-2 rounded-md transition cursor-pointer border-none bg-white mr-2"
              >
                <SearchIcon className="w-5 h-5" />
              </button>
              )}

            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="col-span-9 flex justify-center items-center gap-2">
          <input
            type="text"
            className="w-full px-4 py-2 text-lg border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm transition-all"
            placeholder="Search page..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            onClick={() => {
              setIsSearching(false);
              setSearchQuery('');
            }}
            className="p-2 bg-transparent rounded-md hover:bg-gray-200 transition border-none"
            aria-label="close-search"
          >
            <CloseIcon className="w-6 h-6 text-gray-700" />
          </button>
        </div>

      )}

      {isLanguageEnabled && (
      <div className="col-span-1 flex justify-end items-center">
        <LanguageSelector />
      </div>
      )}
    </div>
  );
};

export default NavBarDesktop;
